import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import TimesheetAnnotator from './TimesheetAnnotator';
import { StatusBadge } from './ui/TableBits';
import { Field } from './ui/formkit';
import { useToast } from './ui/Toast';

// Inline-input styles for the daily items grid. Mirrors TimesheetItemTable,
// with slightly taller inputs because this page is mostly used from phones.
const inputBase = {
  border: '1px solid var(--hairline)', borderRadius: '6px', padding: '9px 10px',
  fontSize: '14px', fontFamily: 'var(--font-sans)', outline: 'none',
  width: '100%', minHeight: '40px', boxSizing: 'border-box',
};
const inputReadonly = { ...inputBase, background: 'var(--surface-soft)', color: 'var(--muted)', cursor: 'default' };
const inputEditable = { ...inputBase, background: 'var(--canvas)', color: 'var(--ink)' };
const inputDisabled = { ...inputBase, background: 'var(--surface-soft)', color: 'var(--muted-soft)', cursor: 'not-allowed' };

// Compact label/value pair for the summary bar.
const SummaryItem = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
    <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted-soft)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
    <span style={{ fontSize: '13px', color: 'var(--ink)' }}>{children}</span>
  </div>
);

// Section wrapper: cream card with a serif title, shared by every block on
// this page.
const SectionCard = ({ title, children, style }) => (
  <section className="card" style={{ marginBottom: '16px', ...style }}>
    <div className="card-body" style={{ padding: '16px' }}>
      <h3 style={{ margin: '0 0 12px', fontFamily: 'var(--font-serif)', fontSize: '20px', fontWeight: 500, letterSpacing: '-0.3px', color: 'var(--ink)' }}>
        {title}
      </h3>
      {children}
    </div>
  </section>
);

// Standalone page shell for guests: no sidebar or top bar, just the wordmark
// band (styled like the login screen's brand mark) over the cream canvas.
// `footer` renders below the content, outside the centered column, so a
// sticky action bar can span the full viewport width.
const GuestShell = ({ children, footer }) => (
  <div style={{ minHeight: '100vh', background: 'var(--canvas)', display: 'flex', flexDirection: 'column' }}>
    <header style={{
      display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
      padding: '12px 24px', borderBottom: '1px solid var(--hairline)', background: 'var(--canvas)',
    }}>
      <svg viewBox="0 0 100 100" width="30" height="30" aria-hidden="true">
        <g>
          <path d="M 83.8 40.9 A 35 35 0 1 1 59.1 16.2" fill="none" stroke="var(--brand-navy)" strokeWidth="10" strokeLinecap="butt" />
          <circle cx="74.7" cy="25.3" r="8" fill="var(--brand-blue)" />
          <path d="M 37 65 L 37 45 A 13 13 0 0 1 63 45 L 63 65" fill="none" stroke="var(--brand-navy)" strokeWidth="10" strokeLinecap="butt" />
        </g>
      </svg>
      <span style={{ fontFamily: 'var(--font-wordmark)', fontSize: '19px', fontWeight: 800, letterSpacing: '-0.5px', lineHeight: 1 }}>
        <span style={{ color: 'var(--brand-blue)' }}>One</span>
        <span style={{ color: 'var(--brand-navy)' }}> Nexus</span>
      </span>
      <span aria-hidden="true" style={{ width: '1px', height: '20px', background: 'var(--hairline)' }} />
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: '14px', color: 'var(--muted)' }}>Timesheet entry</span>
    </header>
    <main style={{ flex: 1, width: '100%', maxWidth: '1280px', margin: '0 auto', padding: '20px 24px 40px' }}>
      {children}
    </main>
    {footer}
  </div>
);

function QuickTime() {
  const toast = useToast();
  const [timesheet, setTimesheet] = useState(null);
  const [timesheetItems, setTimesheetItems] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [newExpense, setNewExpense] = useState({ expenseDate: '', category: 'Travel', amount: '', description: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [previewFiles, setPreviewFiles] = useState([]); // eslint-disable-line no-unused-vars
  const [submitError, setSubmitError] = useState(null);
  const [highlightValidationErrors, setHighlightValidationErrors] = useState(false);
  const [annotationImageUrl, setAnnotationImageUrl] = useState(null);
  const [showAnnotator, setShowAnnotator] = useState(false);
  const pendingImageRef = useRef(null);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const guestCode = queryParams.get('guestCode');
  const appendIndex = queryParams.get('index') == null ? '' : `?index=${queryParams.get('index')}`;

  const isEditable = timesheet?.status === 'Draft' || timesheet?.status === 'Pending' || !timesheet?.status;

  useEffect(() => {
    if (guestCode) {
      verifyGuestCode();
    } else {
      setError('Guest code is required');
      setLoading(false);
    }
  }, [guestCode]);

  useEffect(() => {
    if (timesheet?.id) {
      fetchTimesheetItems();
      fetchAttachments();
      fetchExpenses();
    }
  }, [timesheet?.id]);

  const verifyGuestCode = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/verify${appendIndex}`);
      if (response.status === 404) {
        setError('No Timesheets available for the guest code');
        setLoading(false);
        return;
      }
      if (!response.ok) {
        throw new Error('Invalid guest code');
      }
      const data = await response.json();
      setTimesheet(data);
    } catch (err) {
      setError('Failed to verify guest code: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimesheetItems = async () => {
    if (!timesheet?.id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/ts/${timesheet.id}/items`);
      if (!response.ok) {
        throw new Error('Failed to fetch timesheet items');
      }
      const data = await response.json();
      setTimesheetItems(data);
    } catch (err) {
      setError('Failed to fetch timesheet items: ' + err.message);
      setTimesheetItems([]);
    }
    setLoading(false);
  };

  const fetchAttachments = async () => {
    if (!timesheet?.id) return;

    try {
      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/ts/${timesheet.id}/attachments`);
      if (!response.ok) {
        throw new Error('Failed to fetch attachments');
      }
      const data = await response.json();
      setAttachments(data);
    } catch (err) {
      setError('Failed to fetch attachments');
    }
  };

  const fetchExpenses = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/expenses`);
      if (!response.ok) throw new Error('Failed to fetch expenses');
      const data = await response.json();
      const withReceipts = await Promise.all((data || []).map(async (exp) => {
        try {
          const r = await fetch(`${API_BASE_URL}/guest/${guestCode}/exp/${exp.id}/receipts`);
          exp.receipts = r.ok ? await r.json() : [];
        } catch { exp.receipts = []; }
        return exp;
      }));
      setExpenses(withReceipts);
    } catch {
      setExpenses([]);
    }
  };

  const handleAddExpense = async () => {
    if (!newExpense.expenseDate || !newExpense.amount) {
      toast.error('Expense date and amount are required');
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/expenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newExpense)
      });
      if (!response.ok) throw new Error('Failed to add expense');
      setNewExpense({ expenseDate: '', category: 'Travel', amount: '', description: '' });
      await fetchExpenses();
      toast.success('Expense added');
    } catch {
      toast.error('Failed to add expense');
    }
  };

  const handleExpenseReceiptUpload = async (expenseId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/exp/${expenseId}/receipts`, {
        method: 'POST',
        body: formData
      });
      if (!response.ok) throw new Error('Failed to upload receipt');
      await fetchExpenses();
      toast.success('Receipt uploaded');
    } catch {
      toast.error('Failed to upload receipt');
    }
  };

  const handleExpenseReceiptDownload = async (expenseId, receiptId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/exp/${expenseId}/receipts/${receiptId}`);
      if (!response.ok) throw new Error('Failed to open receipt');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
    } catch {
      toast.error('Failed to open receipt');
    }
  };

  const handleDeleteExpenseReceipt = async (expenseId, receiptId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/exp/${expenseId}/receipts/${receiptId}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete receipt');
      await fetchExpenses();
    } catch {
      toast.error('Failed to delete receipt');
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setSubmitError(null);
      setHighlightValidationErrors(false);

      // Validation: Check non-holiday rows have hours worked and comment
      const missingHours = timesheetItems.filter(item => {
        if (item.type === 'HOLIDAY' || item.type === 'VACATION') return false;
        return !item.hoursWorked || parseFloat(item.hoursWorked) <= 0;
      });

      if (missingHours.length > 0) {
        setHighlightValidationErrors(true);
        throw new Error('All working days must have Hours Worked greater than 0');
      }

      const missingComments = timesheetItems.filter(item => {
        if (item.type === 'HOLIDAY' || item.type === 'VACATION') return false;
        return !item.comment || item.comment.trim() === '';
      });

      if (missingComments.length > 0) {
        setHighlightValidationErrors(true);
        throw new Error('All working days must list the Activities performed on that day');
      }

      // Validation: Check if timesheet requires at least one attachment
      const hasBillableItems = timesheetItems.some(item => item.type === 'BILLABLE');
      const requiresAttachment = hasBillableItems || timesheet?.attachmentRequired;
      if (requiresAttachment && attachments.length === 0) {
        throw new Error('At least one attachment is required for this timesheet');
      }

      // Ensure each item has a type
      const itemsToSubmit = timesheetItems.map(item => ({
        ...item,
        type: item.type || 'BILLABLE'
      }));

      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/ts/${timesheet.id}/items/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(itemsToSubmit)
      });

      if (!response.ok) {
        throw new Error('Failed to submit timesheet');
      }

      // Reload the page on success
      window.location.reload();
    } catch (err) {
      setSubmitError('Failed to submit timesheet: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...timesheetItems];
    // If selecting type and type is HOLIDAY or VACATION, clear relevant fields
    if (field === 'type' && (value === 'HOLIDAY' || value === 'VACATION')) {
      updatedItems[index] = {
        ...updatedItems[index],
        type: value,
        hoursWorked: '',
        extraHours: '',
        totalHours: '',
        comment: ''
      };
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value
      };
    }

    // Calculate total hours when hours worked or extra hours change
    if (field === 'hoursWorked' || field === 'extraHours') {
      const hoursWorked = parseFloat(updatedItems[index].hoursWorked) || 0;
      const extraHours = parseFloat(updatedItems[index].extraHours) || 0;
      updatedItems[index].totalHours = (hoursWorked + extraHours).toFixed(2);
    }

    setTimesheetItems(updatedItems);
  };

  const handleDownload = async (attachmentId, filename) => {
    try {
      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/ts/${timesheet.id}/attachments/${attachmentId}`);
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      // Open in a new tab/window so the browser handles the file (PDF/image/etc.)
      window.open(url, '_blank');
      // Cleanup after the new window has a chance to read the URL
      setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
    } catch (err) {
      toast.error('Failed to download file');
    }
  };

  const handleDelete = async (attachmentId) => {
    if (!timesheet?.id || !isEditable) return;

    try {
      const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/ts/${timesheet.id}/attachments/${attachmentId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }

      await fetchAttachments();
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (!timesheet?.id) return;

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(file => {
      const fileType = file.type.toLowerCase();
      return fileType === 'application/pdf' ||
             fileType === 'image/jpeg' ||
             fileType === 'image/jpg' ||
             fileType === 'image/png';
    });

    if (validFiles.length > 0) {
      setPreviewFiles(validFiles);
      handleUpload(validFiles);
    }
  };

  const handleFileSelect = (e) => {
    if (!timesheet?.id) return;

    const files = Array.from(e.target.files);
    if (!files.length) return;

    const imageFile = files.find(f => f.type.startsWith("image/"));
    const pdfFiles = files.filter(f => f.type === "application/pdf");

    // PDFs → upload immediately (unchanged behavior)
    if (pdfFiles.length) {
      setPreviewFiles(pdfFiles);
      handleUpload(pdfFiles);
    }

    // Images → DO NOT upload yet
    if (imageFile) {
      pendingImageRef.current = imageFile;

      const localUrl = URL.createObjectURL(imageFile);
      setAnnotationImageUrl(localUrl);
      setShowAnnotator(true);
    }
  };

  const handleUpload = async (files) => {
    if (!timesheet?.id) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('timesheetId', timesheet.id);

        const response = await fetch(`${API_BASE_URL}/guest/${guestCode}/ts/${timesheet.id}/attachments`, {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          throw new Error('Failed to upload file');
        }
      }
      await fetchAttachments();
      setPreviewFiles([]);
      toast.success('Files uploaded');
    } catch (err) {
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <GuestShell>
        <div style={{ padding: '64px 16px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-serif)', fontSize: '18px' }}>
          Loading the next available timesheet...
        </div>
      </GuestShell>
    );
  }

  if (error) {
    return (
      <GuestShell>
        <div className="alert error" role="alert">{error}</div>
      </GuestShell>
    );
  }

  const isNonWorking = (item) => item.type === 'HOLIDAY' || item.type === 'VACATION';
  const isWeekend = (item) => item.dayOfWeek === 'Saturday' || item.dayOfWeek === 'Sunday';
  const isErrorRow = (item) => highlightValidationErrors && !isNonWorking(item) &&
    (!item.hoursWorked || parseFloat(item.hoursWorked) <= 0 || !item.comment || item.comment.trim() === '');
  const currIndex = parseInt(queryParams.get('index')) || 0;
  const grandTotal = timesheetItems.reduce((sum, item) => sum + (parseFloat(item.totalHours) || 0), 0).toFixed(2);

  return (
    <GuestShell
      footer={isEditable && (
        <div style={{ position: 'sticky', bottom: 0, zIndex: 20, background: 'var(--canvas)', borderTop: '1px solid var(--hairline)' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '10px 24px', display: 'flex', alignItems: 'center', gap: '10px 16px', flexWrap: 'wrap' }}>
            {submitError && (
              <div className="alert error" role="alert" style={{ flex: '1 1 280px', padding: '8px 12px' }}>{submitError}</div>
            )}
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={!isEditable}
              style={{ marginLeft: 'auto', minWidth: '200px', height: '44px', fontSize: '15px', flex: '0 1 auto' }}
            >
              Submit timesheet
            </button>
          </div>
        </div>
      )}
    >
      {!isEditable && (
        <div className="alert success" role="status" style={{ marginBottom: '16px' }}>
          This timesheet was submitted. It is now read only.
        </div>
      )}

      {/* Timesheet summary bar */}
      {timesheet && (
        <section className="card" style={{ marginBottom: '16px' }}>
          <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px 28px', flexWrap: 'wrap' }}>
            <SummaryItem label="Employee">{timesheet.employeeName}</SummaryItem>
            <SummaryItem label="Employer">{timesheet.corpName}</SummaryItem>
            <SummaryItem label="Client"><span style={{ color: 'var(--muted)' }}>{timesheet.clientName}</span></SummaryItem>
            <SummaryItem label="Period">
              {new Date(timesheet.periodStartDate + 'T00:00:00').toLocaleDateString()} to {new Date(timesheet.periodEndDate + 'T00:00:00').toLocaleDateString()}
            </SummaryItem>
            <SummaryItem label="Total hours"><span style={{ fontWeight: 700, color: 'var(--coral)' }}>{timesheet.totalHours}</span></SummaryItem>
            <StatusBadge status={timesheet.status || 'Pending'} />

            {/* Pager across the guest's timesheets, right-aligned */}
            {typeof timesheet.totalResults === 'number' && timesheet.totalResults > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginLeft: 'auto' }}>
                <button
                  className="btn-secondary"
                  style={{ padding: '0 14px' }}
                  disabled={currIndex <= 0}
                  onClick={() => {
                    if (currIndex > 0) {
                      const params = new URLSearchParams(location.search);
                      params.set('index', currIndex - 1);
                      window.location.search = params.toString();
                    }
                  }}
                >
                  Previous
                </button>
                <span style={{ fontFamily: 'var(--font-sans)', fontSize: '13px', fontWeight: 500, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                  {`Timesheet ${currIndex + 1} of ${timesheet.totalResults}`}
                </span>
                <button
                  className="btn-secondary"
                  style={{ padding: '0 14px' }}
                  disabled={currIndex >= timesheet.totalResults - 1}
                  onClick={() => {
                    if (currIndex < timesheet.totalResults - 1) {
                      const params = new URLSearchParams(location.search);
                      params.set('index', currIndex + 1);
                      window.location.search = params.toString();
                    }
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Daily items */}
      <SectionCard title="Daily hours">
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Day</th>
                <th>Type</th>
                <th>Hours Worked</th>
                <th>Extra Hours</th>
                <th>Total Hours</th>
                <th>Activities</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(timesheetItems) && timesheetItems.length > 0 ? (
                <>
                  {timesheetItems.map((item, index) => (
                    <tr
                      key={item.id || index}
                      style={{
                        background: isErrorRow(item)
                          ? 'var(--danger-bg)'
                          : (isNonWorking(item) || isWeekend(item)) ? 'var(--surface-soft)' : 'var(--canvas)'
                      }}
                    >
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <input
                          type="date"
                          value={item.calendarDate ? new Date(item.calendarDate).toISOString().split('T')[0] : ''}
                          readOnly
                          style={{ ...inputReadonly, minWidth: '130px', color: (isNonWorking(item) || isWeekend(item)) ? 'var(--muted-soft)' : inputReadonly.color }}
                        />
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <input
                          type="text"
                          value={item.dayOfWeek || ''}
                          readOnly
                          style={{ ...inputReadonly, width: '100px', color: (isNonWorking(item) || isWeekend(item)) ? 'var(--muted-soft)' : inputReadonly.color }}
                        />
                      </td>
                      <td>
                        <select
                          value={item.type || 'BILLABLE'}
                          onChange={(e) => handleItemChange(index, 'type', e.target.value)}
                          disabled={!isEditable}
                          style={{ ...inputBase, background: isNonWorking(item) ? 'var(--surface-soft)' : 'var(--canvas)', color: 'var(--ink)', cursor: isEditable ? 'pointer' : 'not-allowed', width: '130px' }}
                        >
                          <option value="BILLABLE">Billable</option>
                          <option value="HOLIDAY">Holiday</option>
                          <option value="VACATION">Vacation</option>
                          <option value="NON_BILLABLE">Non-Billable</option>
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          value={item.hoursWorked || ''}
                          onChange={(e) => handleItemChange(index, 'hoursWorked', e.target.value)}
                          disabled={!isEditable || isNonWorking(item)}
                          style={{ ...((!isEditable || isNonWorking(item)) ? inputDisabled : inputEditable), width: '96px' }}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          value={item.extraHours || ''}
                          onChange={(e) => handleItemChange(index, 'extraHours', e.target.value)}
                          disabled={!isEditable || isNonWorking(item)}
                          style={{ ...((!isEditable || isNonWorking(item)) ? inputDisabled : inputEditable), width: '96px' }}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={item.totalHours || '0.00'}
                          readOnly
                          style={{ ...inputReadonly, width: '96px' }}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={item.comment || ''}
                          onChange={(e) => handleItemChange(index, 'comment', e.target.value)}
                          disabled={!isEditable || isNonWorking(item)}
                          style={{ ...((!isEditable || isNonWorking(item)) ? inputDisabled : inputEditable), minWidth: '320px' }}
                          placeholder="Describe the work performed"
                        />
                      </td>
                    </tr>
                  ))}
                  {/* Summary Row */}
                  <tr style={{ background: 'var(--surface-soft)', borderTop: '2px solid var(--hairline)' }}>
                    <td colSpan="5" style={{ textAlign: 'right', fontWeight: 700, fontSize: '13px', color: 'var(--muted)' }}>Total hours:</td>
                    <td>
                      <input
                        type="number"
                        value={grandTotal}
                        readOnly
                        style={{ ...inputReadonly, width: '96px', fontWeight: 700, color: 'var(--ink)' }}
                      />
                    </td>
                    <td></td>
                  </tr>
                </>
              ) : (
                <tr className="state-row">
                  <td colSpan="7">No timesheet items found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Attachments and Expenses side by side on desktop, stacked on phones */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))', gap: '16px', alignItems: 'start' }}>

      {/* Attachments Section */}
      <SectionCard title="Attachments" style={{ marginBottom: 0 }}>
        {isEditable && (
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-upload').click()}
            style={{
              border: `2px dashed ${dragActive ? 'var(--coral)' : 'var(--hairline)'}`,
              borderRadius: 'var(--radius-lg)',
              padding: '20px 16px',
              textAlign: 'center',
              background: dragActive ? 'var(--surface-card)' : 'var(--surface-soft)',
              transition: 'border-color 0.15s, background 0.15s',
              marginBottom: '16px',
              cursor: 'pointer'
            }}
          >
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={dragActive ? 'var(--coral)' : 'var(--muted-soft)'} strokeWidth="1.5" style={{ marginBottom: '8px' }} aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <p style={{ margin: '0 0 8px', fontSize: '14px', color: 'var(--muted)' }}>
              {dragActive ? 'Drop files here' : 'Upload the approved timesheet. Tap to browse or drop files here.'}
            </p>
            <span style={{ fontSize: '12px', color: 'var(--muted-soft)' }}>Accepted: PDF, PNG, JPG, JPEG</span>
            <input hidden
              type="file"
              multiple
              onChange={handleFileSelect}
              accept=".pdf,.jpg,.jpeg,.png"
              id="file-upload"
            />
          </div>
        )}

        {uploading && (
          <div role="status" style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '12px' }}>
            Uploading files...
          </div>
        )}

        {/* Attachments List */}
        {attachments.length > 0 && (
          <div style={{ border: '1px solid var(--hairline)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', background: 'var(--surface-soft)', fontSize: '12px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--hairline)' }}>
              Uploaded files
            </div>
            {attachments.map((attachment) => (
              <div key={attachment.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '12px 14px', borderBottom: '1px solid var(--hairline-soft)' }}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownload(attachment.id, attachment.filename);
                  }}
                  className="table-link"
                  title="Open"
                  style={{ fontSize: '13px', overflowWrap: 'anywhere' }}
                >
                  {attachment.filename}
                </a>
                {isEditable && (
                  <button
                    onClick={() => handleDelete(attachment.id)}
                    title="Delete"
                    aria-label={`Delete ${attachment.filename}`}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '10px', display: 'flex', alignItems: 'center', color: 'var(--danger)', flexShrink: 0 }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6M14 11v6M9 6V4h6v2" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Expenses Section */}
      <SectionCard title="Expenses" style={{ marginBottom: 0 }}>
        {isEditable && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', alignItems: 'end', marginBottom: '14px' }}>
            <Field label="Date">
              <input type="date" value={newExpense.expenseDate}
                onChange={e => setNewExpense(p => ({ ...p, expenseDate: e.target.value }))}
                style={inputEditable} />
            </Field>
            <Field label="Category">
              <select value={newExpense.category}
                onChange={e => setNewExpense(p => ({ ...p, category: e.target.value }))}
                style={{ ...inputEditable, cursor: 'pointer' }}>
                {['Travel', 'Meals', 'Lodging', 'Supplies', 'Mileage', 'Other'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Amount">
              <input type="number" step="0.01" min="0" value={newExpense.amount}
                onChange={e => setNewExpense(p => ({ ...p, amount: e.target.value }))}
                style={inputEditable} />
            </Field>
            <Field label="Description">
              <input type="text" value={newExpense.description}
                onChange={e => setNewExpense(p => ({ ...p, description: e.target.value }))}
                style={inputEditable} />
            </Field>
            <button className="btn-secondary" onClick={handleAddExpense}>
              Add expense
            </button>
          </div>
        )}

        {expenses.length > 0 ? (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Category</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Receipts</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map(exp => (
                  <tr key={exp.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{exp.expenseDate}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{exp.category}</td>
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{exp.amount}</td>
                    <td>{exp.description}</td>
                    <td style={{ whiteSpace: 'nowrap' }}><StatusBadge status={exp.status} /></td>
                    <td>
                      {(exp.receipts || []).map(r => (
                        <span key={r.id} style={{ display: 'inline-flex', alignItems: 'center', marginRight: '10px' }}>
                          <a href="#" onClick={(e) => { e.preventDefault(); handleExpenseReceiptDownload(exp.id, r.id); }}
                            className="table-link" style={{ fontSize: '13px' }}>{r.filename}</a>
                          {isEditable && (
                            <button onClick={() => handleDeleteExpenseReceipt(exp.id, r.id)}
                              title="Delete receipt"
                              aria-label={`Delete receipt ${r.filename}`}
                              style={{ marginLeft: '2px', padding: '8px', color: 'var(--danger)', border: 'none', background: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            </button>
                          )}
                        </span>
                      ))}
                      {isEditable && (
                        <label className="btn-link" style={{ display: 'inline-block', padding: '8px 0', cursor: 'pointer' }}>
                          + Add receipt
                          <input hidden type="file" accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => { handleExpenseReceiptUpload(exp.id, e.target.files[0]); e.target.value = ''; }} />
                        </label>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--muted-soft)' }}>No expenses added.</p>
        )}
      </SectionCard>

      </div>

      {showAnnotator && annotationImageUrl && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(24, 23, 21, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => {
            URL.revokeObjectURL(annotationImageUrl);
            setShowAnnotator(false);
            setAnnotationImageUrl(null);
          }}
        >
          <div
            style={{
              width: '95vw',
              maxWidth: '1200px',
              height: '90vh',
              backgroundColor: 'var(--canvas)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--hairline)',
              padding: '16px',
              position: 'relative',
              boxShadow: 'var(--shadow-xl)',
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <p style={{ margin: '0 0 12px', fontFamily: 'var(--font-sans)', fontSize: '14px', fontWeight: 500, color: 'var(--ink)' }}>
              Click the approximate center of the hours worked and drag. Make sure it matches the total billable hours.
            </p>

            {/* Annotator */}
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <TimesheetAnnotator
                imageUrl={annotationImageUrl}
                originalFileName={pendingImageRef.current?.name}
                onClose={() => {
                  URL.revokeObjectURL(annotationImageUrl);
                  setShowAnnotator(false);
                  setAnnotationImageUrl(null);
                }}
                onSave={(annotatedFile) => {
                  handleUpload([annotatedFile]);

                  pendingImageRef.current = null;

                  URL.revokeObjectURL(annotationImageUrl);
                  setShowAnnotator(false);
                  setAnnotationImageUrl(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </GuestShell>
  );
}

export default QuickTime;
